import { fallbackPortfolioData, type PortfolioData } from "../data/portfolio";
import { getFirebaseApp } from "./firebase";

const portfolioCollection = "portfolio";
const portfolioDocumentId = "xander";

type PartialPortfolioData = Partial<PortfolioData>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readArray<T>(value: unknown, fallback: T[]) {
  return Array.isArray(value) ? (value as T[]) : fallback;
}

function mergePortfolioData(value: unknown): PortfolioData {
  if (!isRecord(value)) {
    return fallbackPortfolioData;
  }

  const incoming = value as PartialPortfolioData;
  const hasCvDossier = Array.isArray(incoming.cv?.sections);
  const incomingCvVersion =
    typeof incoming.cv?.contentVersion === "number" ? incoming.cv.contentVersion : 0;
  const fallbackCvVersion = fallbackPortfolioData.cv.contentVersion;
  const useIncomingCv = hasCvDossier && incomingCvVersion >= fallbackCvVersion;

  return {
    public: typeof incoming.public === "boolean" ? incoming.public : fallbackPortfolioData.public,
    profile: {
      ...fallbackPortfolioData.profile,
      ...(isRecord(incoming.profile) ? incoming.profile : {}),
      links: readArray(incoming.profile?.links, fallbackPortfolioData.profile.links),
    },
    about: {
      ...fallbackPortfolioData.about,
      ...(isRecord(incoming.about) ? incoming.about : {}),
      paragraphs: readArray(incoming.about?.paragraphs, fallbackPortfolioData.about.paragraphs),
      highlights: readArray(incoming.about?.highlights, fallbackPortfolioData.about.highlights),
    },
    education: {
      ...fallbackPortfolioData.education,
      ...(isRecord(incoming.education) ? incoming.education : {}),
      courses: readArray(incoming.education?.courses, fallbackPortfolioData.education.courses),
    },
    awards: readArray(incoming.awards, fallbackPortfolioData.awards),
    memberships: readArray(incoming.memberships, fallbackPortfolioData.memberships),
    seniorProjects: readArray(incoming.seniorProjects, fallbackPortfolioData.seniorProjects),
    publications: readArray(incoming.publications, fallbackPortfolioData.publications),
    experiences: readArray(incoming.experiences, fallbackPortfolioData.experiences),
    cv: {
      ...fallbackPortfolioData.cv,
      ...(useIncomingCv && isRecord(incoming.cv) ? incoming.cv : {}),
      methodsNote: {
        ...fallbackPortfolioData.cv.methodsNote,
        ...(useIncomingCv && isRecord(incoming.cv?.methodsNote) ? incoming.cv.methodsNote : {}),
        tools: readArray(
          useIncomingCv ? incoming.cv?.methodsNote?.tools : undefined,
          fallbackPortfolioData.cv.methodsNote.tools,
        ),
      },
      sections: readArray(
        useIncomingCv ? incoming.cv?.sections : undefined,
        fallbackPortfolioData.cv.sections,
      ),
    },
  };
}

export async function loadPortfolioData(): Promise<PortfolioData> {
  const app = getFirebaseApp();

  if (!app) {
    return fallbackPortfolioData;
  }

  try {
    const { doc, getDoc, getFirestore } = await import("firebase/firestore");
    const db = getFirestore(app);
    const snapshot = await getDoc(doc(db, portfolioCollection, portfolioDocumentId));

    if (!snapshot.exists()) {
      return fallbackPortfolioData;
    }

    return mergePortfolioData(snapshot.data());
  } catch (error) {
    console.info("Portfolio content loaded from the local fallback.", error);
    return fallbackPortfolioData;
  }
}
